import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';

/**
 * Dedicated Reverb/Echo instance for the CBT live monitoring feature.
 *
 * The app-wide `@/services/echo` instance points its auth endpoint at
 * Lighthouse's GraphQL subscription auth (`/graphql/subscriptions/auth`).
 * That endpoint only authorizes channels registered as GraphQL
 * subscriptions — it returns 403 for plain `Broadcast::channel(...)`
 * channels like ours (`school.{id}.cbt-monitoring`).
 *
 * Rather than refactoring the global Echo (which would break Apollo
 * subscriptions across the app), this feature ships its own Echo instance
 * pointed at the dedicated Sanctum-auth route on the backend
 * (`/api/cbt-monitoring/channel-auth`) which delegates to Laravel's
 * `Broadcast::auth($request)`.
 *
 * Cost: one additional WebSocket connection to the same Reverb server.
 * Acceptable for an opt-in premium feature; revisit if the app gains
 * more plain-broadcast channels.
 */

if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).Pusher = Pusher;
}

const authHeaders: Record<string, string> = {
  'Apollo-Require-Preflight': 'true',
};

Object.defineProperty(authHeaders, 'Authorization', {
  get: () => `Bearer ${localStorage.getItem(LocalStorageKeys.token)}`,
  enumerable: true,
});

Object.defineProperty(authHeaders, 'X-Current-School-ID', {
  get: () => localStorage.getItem(LocalStorageKeys.currentSchoolId) ?? '',
  enumerable: true,
});

const backendBase = (import.meta.env.VITE_BACKEND_API_URL as string).replace(/\/graphql\/?$/, '');
const authEndpoint = backendBase.replace(/\/$/, '') + '/api/cbt-monitoring/channel-auth';

export const monitoringEcho = new Echo({
  broadcaster: 'reverb',
  key: import.meta.env.VITE_REVERB_APP_KEY,
  wsHost: import.meta.env.VITE_REVERB_HOST,
  wsPort: import.meta.env.VITE_REVERB_PORT ?? 80,
  wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
  forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
  enabledTransports: ['ws', 'wss'],
  disableStats: true,
  authEndpoint,
  auth: {
    headers: authHeaders,
  },
});
